import { useInfiniteQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { QueryState } from '@/shared/ui/QueryState'
import { api } from '@/shared/lib/api'
import { CERT_LABEL, makeLabeler, useCommonCodes, useTargetCodes } from '@/shared/lib/codes'
import { SolutionListMobile } from '@/public/features/solution/ui/SolutionListMobile'
import { SolutionListPc } from '@/public/features/solution/ui/SolutionListPc'

const PAGE_SIZE = 24

export function SolutionListPage() {
  const [category, setCategory] = useState('')
  const [target, setTarget] = useState('')
  const [helpType, setHelpType] = useState('')
  const [serviceArea, setServiceArea] = useState('')
  const [q, setQ] = useState('')

  const cards = useInfiniteQuery({
    queryKey: ['solution-cards', { category, target, helpType, serviceArea, q }],
    queryFn: ({ pageParam }) =>
      api.solutionCards({ category, target, helpType, serviceArea, q, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.items.length, 0)
      return loaded < last.total ? loaded : undefined
    },
  })

  const loaded = cards.data?.pages.flatMap((page) => page.items) ?? []

  const categories = useCommonCodes('category')
  const targets = useTargetCodes()
  const helpTypes = useCommonCodes('help_type')
  const areas = useCommonCodes('service_area')

  const categoryLabel = makeLabeler(categories.data)
  const helpTypeLabel = makeLabeler(helpTypes.data)

  return (
    <>
      <SolutionListMobile query={cards} />
      <SolutionListPc query={cards} />

      <div className="desktop-screen">
      <h1 className="m-0 mb-1.5 text-headline-sm font-bold tracking-[-0.02em]">기업해결카드</h1>
      <p className="m-0 mb-7 text-body-sm text-black-100">
        지역 문제를 해결할 수 있는 기업의 해결카드입니다. 한 기업이 여러 개의 카드를 가질 수
        있습니다.
      </p>

      <div className="mb-5 flex flex-wrap gap-2">
        <select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="분야">
          <option value="">분야 전체</option>
          {categories.data?.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </select>
        <select value={target} onChange={(e) => setTarget(e.target.value)} aria-label="대상">
          <option value="">대상 전체</option>
          {targets.data?.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </select>
        <select value={helpType} onChange={(e) => setHelpType(e.target.value)} aria-label="도움방식">
          <option value="">도움방식 전체</option>
          {helpTypes.data?.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </select>
        <select
          value={serviceArea}
          onChange={(e) => setServiceArea(e.target.value)}
          aria-label="서비스 지역"
        >
          <option value="">서비스 지역 전체</option>
          {areas.data?.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </select>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="제목·요약 검색"
          aria-label="검색어"
        />
      </div>

      <QueryState
        isPending={cards.isPending}
        error={cards.error}
        isEmpty={loaded.length === 0}
        emptyMessage="조건에 맞는 기업카드가 없습니다."
      >
        <p className="text-black-100 text-label" style={{ marginBottom: 12 }}>
          {cards.data?.pages[0]?.total}개 카드
        </p>
        <div className="grid grid-cols-1 gap-3.5 min-[601px]:grid-cols-2 min-[901px]:grid-cols-3">
          {loaded.map((c) => (
            <Link key={c.id} className="block rounded-[10px] border border-white-300 bg-white-50 p-[18px] transition-[border-color] duration-150 ease-linear hover:border-white-400" to={`/solutions/${c.id}`}>
              <div className="mb-2 text-caption text-white-900">
                {c.org.name}
                {c.org.certType && CERT_LABEL[c.org.certType] ? (
                  <>
                    {' '}
                    <span className={c.org.certType === 'cert_certified' ? 'inline-block rounded-full border border-white-400 px-2 py-0.5 text-[11px] font-semibold border-black-600 bg-black-600 text-white-50' : 'inline-block rounded-full border border-white-400 px-2 py-0.5 text-[11px] font-semibold bg-white-200 text-black-500'}>
                      {CERT_LABEL[c.org.certType]}
                    </span>
                  </>
                ) : null}
              </div>
              <h3>{c.title}</h3>
              <p>{c.summary}</p>
              <div className="flex flex-wrap gap-[5px]">
                {c.categoryCodes.map((code) => (
                  <span key={code} className="rounded-full bg-white-200 px-[9px] py-[3px] text-[11px] text-black-500">
                    {categoryLabel(code)}
                  </span>
                ))}
                {c.helpTypeCodes.map((code) => (
                  <span key={code} className="rounded-full bg-white-200 px-[9px] py-[3px] text-[11px] text-black-500">
                    {helpTypeLabel(code)}
                  </span>
                ))}
              </div>
            </Link>
          ))}
        </div>
      </QueryState>
      </div>
    </>
  )
}
